import React, { useEffect, useRef } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import CardUI from '../../../common/Card';
import HeaderText from '../../../common/HeaderText';
import ProfilePicture from '../../../common/ProfilePicture';
import ChatSkeleton from '../../../components/ChatSkeleton';
import ChatProfileSkeleton from '../../../components/ChatProfileSkeleton';
import { SendIcon } from '../../../assets/constants';
import { onlyDateFormat } from '../../../utils/helpers';

function sentByAgent(message, agent) {
  if (message.senderType) {
    return message.senderType === 'agent' && agent?.id != null &&
      message.senderId != null && String(message.senderId) === String(agent.id);
  }
  // Older messages identify their sender using the communication-service ID.
  const senderCommId = message.senderCommId || message.sender?.communicationUserId;
  return !!agent?.azure_comm_id && senderCommId === agent.azure_comm_id;
}

// The established case chat presentation, independent of its message transport.
export default function ChatView({ caseDetail, loadingCaseDetail, agent, messages, loading,
  text, onTextChange, onSubmit, sending }) {
  const chatWindowRef = useRef(null);
  const client = caseDetail?.case?.client;
  const rows = [...(messages || [])].reverse();
  const latestMessageId = rows[rows.length - 1]?.id;
  useEffect(() => {
    const chatWindow = chatWindowRef.current;
    if (chatWindow) chatWindow.scrollTop = chatWindow.scrollHeight;
  }, [latestMessageId, rows.length]);

  return <Box display="flex" width="100%" alignItems="flex-start" justifyContent="space-between" gap={3}>
    <Box width="100%" minWidth={0} boxShadow="0px 2px 16px 0px rgba(0, 0, 0, 0.08)"
      padding="24px 32px" borderRadius="8px" height="563px" position="relative">
      {loading ? <Box role="status" aria-label="Loading chat"><ChatSkeleton /></Box> : rows.length ?
        <Box aria-label="Messages" height="calc(100% - 64px)" sx={{ overflowY: 'auto' }} padding="6px"
          ref={chatWindowRef} display="flex" flexDirection="column"
          justifyContent={rows.length > 4 ? 'flex-start' : 'flex-end'} position="relative">
          {rows.map((message, index) => {
            const ownMessage = sentByAgent(message, agent);
            const showDateSeparator = index === 0 || new Date(message.createdOn).toLocaleDateString() !==
              new Date(rows[index - 1].createdOn).toLocaleDateString();
            return <Stack key={message.id} mb={2} gap="3px" alignItems={ownMessage ? 'flex-end' : 'flex-start'}>
              {showDateSeparator && <Box textAlign="center" width="100%" mb={2}>
                <Typography variant="body1" color="rgba(0, 0, 0, 0.38)" fontSize="14px">
                  {onlyDateFormat(message.createdOn)}
                </Typography>
              </Box>}
              <Box bgcolor={ownMessage ? '#0C80F3' : '#ECECEC'} borderRadius="8px"
                padding="16px 24px" maxWidth="554px" width="100%">
                {!ownMessage && <Typography fontSize="12px" color="#0D0D0D" variant="body1" pb={1}>
                  {message.senderDisplayName}
                </Typography>}
                <Typography fontSize="14px" color={ownMessage ? '#fff' : '#0D0D0D'} variant="body1"
                  sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {message.content?.message}
                </Typography>
              </Box>
              <Box display="flex" gap="4px" alignItems="center">
                <Typography variant="body1" fontSize="12px" color="rgba(0, 0, 0, 0.38)">
                  {onlyDateFormat(message.createdOn)}
                </Typography>
                <Typography variant="body1" fontSize="12px" color="rgba(0, 0, 0, 0.38)">
                  {new Date(message.createdOn).toLocaleTimeString()}
                </Typography>
              </Box>
            </Stack>;
          })}
        </Box> : <Box height="100%" display="flex" width="100%" alignItems="flex-end"
          justifyContent="center" paddingBottom="85px">
          <Typography color="secondary" variant="body1">Start Chatting with client</Typography>
        </Box>}
      <Box display="flex" alignItems="center" justifyContent="space-between" gap="16px" component="form"
        width="100%" p="32px" position="absolute" left="0" right="0" bottom="0" onSubmit={onSubmit}>
        <Box flex="1">
          <TextField name="message" placeholder="Write your message here" size="small" fullWidth
            autoComplete="off" value={text} onChange={event => onTextChange(event.target.value)}
            inputProps={{ maxLength: 8000, 'aria-label': 'Message' }} disabled={sending} />
        </Box>
        <Button startIcon={<SendIcon aria-hidden="true" />} variant="contained" type="submit" disabled={sending || !text.trim()}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </Box>
    </Box>
    <CardUI maxWidth="260px" dashboard="true" padding="32px">
      <Stack gap="24px" alignItems="center">
        <Typography variant="h3" fontSize="16px" fontWeight="500" color="secondary" alignSelf="flex-start">
          CHATTING WITH
        </Typography>
        {loadingCaseDetail ? <ChatProfileSkeleton /> : <>
          <ProfilePicture width="134px" height="134px" src={client?.profile_picture_url} alt={client?.name} />
          <Stack gap="9px" alignItems="center">
            <HeaderText textContent={client?.name} fontSize="28px" fontWeight="500" color="#000"
              variant="h3" textAlign="center" />
            <Typography variant="body1" color="#0D0D0D" fontWeight="400" fontSize="14px">{client?.email}</Typography>
            <Typography variant="body1" color="#0D0D0D" fontWeight="400" fontSize="14px">{client?.contact_number}</Typography>
          </Stack>
        </>}
      </Stack>
    </CardUI>
  </Box>;
}
